import { LogEntry, LogEntryMeta, RequestLogEntry } from './logEntry';

/**
 * CORRELATION_META_KEY is the meta key for correlation IDs.
 *
 * FIXME: Find a better home for this value.
 */
const CORRELATION_META_KEY = 'correlationId';

/**
 * LogMethod is a basic interface to define a log method on a logger which
 * takes a full entry.
 */
interface LogMethod {
  (entry: LogEntry): void;
}

/**
 * LeveledLogMethod describes acceptable signatures for logging with an implicit
 * (such as logger.error).
 */
interface LeveledLogMethod {
  (message: LogEntry['message'], meta?: LogEntryMeta): void;
  (entry: LogEntry): void;
}

/**
 * RequestLogMethod describes acceptable signatures for helper methods related to
 * logging requests.
 */
interface RequestLogMethod {
  (entry: RequestLogEntry): void;
}

/**
 * Logger is a simple logger interface supporting default meta data,
 * creating child loggers, and all basic logging methods.
 */
interface Logger {
  /**
   * defaultMeta is a property with the current default meta data
   * appended to every log message
   */
  defaultMeta: LogEntryMeta;

  /**
   * log logs an entry at the given level.
   */
  log: LogMethod;

  /**
   * request is a helper log method for logging HTTP requests. The appropriate
   * log level will be determined by the implementation.
   */
  request: RequestLogMethod;

  /**
   * trace logs at `LogLevel.TRACE`
   */
  trace: LeveledLogMethod;

  /**
   * debug logs at `LogLevel.DEBUG`
   */
  debug: LeveledLogMethod;

  /**
   * info logs at `LogLevel.INFO`
   */
  info: LeveledLogMethod;

  /**
   * warn logs at `LogLevel.WARN`
   */
  warn: LeveledLogMethod;

  /**
   * error logs at `LogLevel.ERROR`
   */
  error: LeveledLogMethod;

  /**
   * child returns a new instance of `Logger` based on the current instance.
   *
   * Using `child` will copy the current logger's configuration, current
   * default meta data, and append any additional meta data provided.
   */
  child(additionalMeta: Partial<LogEntryMeta>): Logger;

  /**
   * appendMeta adds the given meta data to the current logger's
   * `defaultMeta`.
   */
  appendMeta(meta: Partial<LogEntryMeta>): void;

  /**
   * getMeta returns the current `defaultMeta` value for `key`.
   */
  getMeta<T>(key: string): T | undefined;

  /**
   * generateCorrelationId returns a new correlation ID.
   *
   * This method does not change the logger in any way.
   */
  generateCorrelationId(): string;

  /**
   * getCorrelationId returns the current correlation ID set in
   * `defaultMeta` or undefined.
   */
  getCorrelationId(): string | undefined;

  /**
   * setCorrelationId sets a given correlation ID or generates and
   * assigns a new one if `id` is `undefined`.
   */
  setCorrelationId(id?: string): void;
}

export type { Logger, LeveledLogMethod, LogMethod, RequestLogMethod };
export { CORRELATION_META_KEY };
